import {Component, OnInit, Input, Output, EventEmitter, inject, ViewChild, ElementRef, Signal} from '@angular/core';
import {NgFor, NgIf, CommonModule} from '@angular/common';
import {HttpClient} from '@angular/common/http';
import {environment} from 'src/environments/environment';
import {ActivatedRoute, Router} from '@angular/router';
import {FormsModule} from '@angular/forms';
import {LoaderComponent} from '../../loader/loader.component';
import {DashboardService} from 'src/app/services/dashboard/dashboard.service';
import {compareTabData, retailerData, rankingData, sectorData, clusterData} from "./compareDialogInterfaces";
import {NodeRuntime} from "node:inspector";
import {toSignal} from "@angular/core/rxjs-interop";
import {map} from "rxjs";
import {MediaQueryService} from "../../../../services/media-query/media-query.service";

@Component({
  selector: 'app-compare-dialog',
  templateUrl: './compare-dialog.component.html',
  styleUrls: ['./compare-dialog.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    NgFor,
    FormsModule,
    NgIf,
    LoaderComponent,
  ]
})
export class CompareDialogComponent implements OnInit {

  constructor(
    private http: HttpClient,
    private router: Router,
    private route: ActivatedRoute
  ) {

  }

  @Input() initialRCL: any = null;
  @Input() mainRetailer: any = null
  @Input() mainList: any = null
  @Input() preSelectedComparisons: (retailerData | rankingData | sectorData | clusterData)[] | null = null
  @Output() closeDialog = new EventEmitter<void>();
  @Output() selectedListsChange = new EventEmitter<any[]>(); // Replace `any[]` with your specific type if available

  @ViewChild('tabContainer') tabContainer!: ElementRef;
  @ViewChild('averageCheckbox') averageCheckbox!: ElementRef<HTMLInputElement>;
  tabs: compareTabData[] = []

  activeTabIndex = 0;
  private mediaService: MediaQueryService = inject(MediaQueryService);
  public isMobile: Signal< boolean > = toSignal(this.mediaService.getDisplayType().pipe(map(({isMobile}) => isMobile)), {initialValue: window.innerWidth < 768});


  dashboardService = inject(DashboardService)
  isLoading: boolean = true;

  retailers: retailerData[] = []
  retailersCopy: retailerData[] = []
  selectedRetailers: retailerData[] = [];

  lists: rankingData[] = []
  listsCopy: rankingData[] = []
  selectedLists: rankingData[] = [];

  sectors: sectorData[] = []
  sectorsCopy: sectorData[] = []
  selectedSectors: sectorData[] = [];

  clusters: clusterData[] = []
  clustersCopy: clusterData[] = []
  selectedClusters: clusterData[] = [];

  filteredRetailers: retailerData[] = []
  filteredLists: rankingData[] = []
  filteredSectors: sectorData[] = []
  filteredClusters: clusterData[] = []

  combinedSelected: (retailerData | rankingData | sectorData | clusterData)[] = [];

  profileType: string = '';
  current_retailer_dict: retailerData | null = null;
  current_list_dict: rankingData | null = null;

  defaultList: rankingData = {
    logo: "",
    name: "",
    l_id: null,
    r_id: null,
    region_code: null,
    region_name: null,
    rcl: null,
    sector_name: "",
    type: "",
  }

  searchTexts: { [key: string]: string } = {
    rankings: '',
    sectors: '',
    retailers: '',
    clusters: '',
  };
  selectedIndex = 0;
  topCompareSelector: rankingData[] = []
  currentTopList: rankingData | null = null;
  currentListShowing: rankingData | null = null;

  ngOnInit() {
    this.setPreSelectedComparisons()

    let url = `${environment.backend_base}main/retailer/get_rcls_to_compare/`
    let payload = {
      rcl: this.initialRCL
    }
    this.http.post(url, payload).subscribe((response: any) => {

      this.profileType = response.profile_type;
      this.current_retailer_dict = response.current_retailer_dict;
      this.current_list_dict = response.current_list_dict;


      this.setInitialData(response);
      this.setInitialFilters(response);
      this.updateTabData();
      this.setTopCompareSelector();

      if (this.current_list_dict) {
        if (this.preSelectedComparisons && this.preSelectedComparisons.length > 0) {
          const listItem = this.preSelectedComparisons.find((item: any) => item.type === 'list') as rankingData;

          if (listItem) {
            this.onRankingSelect(listItem);
            this.manualToggleRankingAverageCheckbox(true)
          } else {
            this.onListSelectedByItem(this.current_list_dict);
          }
        }

        this.updateFilteredData(this.current_list_dict);
        this.makeMockSelection(this.current_list_dict);
      }
      this.isLoading = false;
    })
  }

  manualToggleRankingAverageCheckbox(settingBoolean:boolean) {
    setTimeout(() => {
      const checkbox = this.averageCheckbox?.nativeElement;
      if (checkbox) {
        checkbox.checked = settingBoolean;
        checkbox.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  setPreSelectedComparisons() {
    if (this.preSelectedComparisons) {
      this.preSelectedComparisons.forEach((item: any) => {
        switch (item.type) {
          case 'retailer':
            this.selectedRetailers.push(item);
            break;
          case 'list':
            this.selectedLists.push(item);
            break;
          case 'sector':
            const cleanedSector = { ...item };
            cleanedSector.name = item.name.replace(/\s*\(.*?\)\s*/g, '').trim();
            this.selectedSectors.push(cleanedSector);
            break;
          case 'cluster':
            const cleanedCluster = { ...item };
            cleanedCluster.name = item.name.replace(/\s*\(.*?\)\s*/g, '').trim();
            this.selectedClusters.push(cleanedCluster);
            break;
        }
      });

      this.updateCombinedSelected();
    }
  }

  makeMockSelection(current_list_dict: rankingData) {
    const index = this.topCompareSelector.findIndex(
      list => list.rcl === current_list_dict.rcl
    );

    this.selectedIndex = index;

  }

  addCompareParameter() {
    if (this.profileType === 'retailer' || this.profileType === 'retailer_in_list') {
      if (this.currentTopList) {
        if (this.currentTopList?.rcl != null) {
          const baseUrl = this.router.url.split('?')[0]; // Remove existing query params
          const params = new URLSearchParams({
            id: this.currentTopList.rcl.toString(),
            compare: this.joinRCLIds()
          }).toString();

          this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { id: this.currentTopList.rcl.toString(), compare: this.joinRCLIds() },
            queryParamsHandling: 'merge'
          });

          window.location.href = `${baseUrl}?${params}`;
        } else {
          console.warn("currentTopList or rcl is null");
        }
      } else {
        const currentParams = new URLSearchParams(window.location.search);

        //currentParams.set('compare', this.joinRCLIds());
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { compare: this.joinRCLIds() },
          queryParamsHandling: 'merge'
        });



        //const baseUrl = this.router.url.split('?')[0]; // removes old query params
        //const newUrl = `${baseUrl}?${currentParams.toString()}`;

        //window.location.href = newUrl;
      }

    } else if (this.profileType === 'list') {
      if (this.currentTopList && this.current_list_dict && this.currentTopList.rcl !== this.current_list_dict.rcl) {
        if (this.currentTopList?.rcl != null) {
          const baseUrl = this.router.url.split('?')[0]; // Remove existing query params
          const params = new URLSearchParams({
            id: this.currentTopList.rcl.toString(),
            compare: this.joinRCLIds()
          }).toString();

          window.location.href = `${baseUrl}?${params}`;
        } else {
          console.warn("currentTopList or rcl is null");
        }
      }
      else {
        const currentParams = new URLSearchParams(window.location.search);

        currentParams.set('compare', this.joinRCLIds());

        const baseUrl = this.router.url.split('?')[0]; // removes old query params
        const newUrl = `${baseUrl}?${currentParams.toString()}`;

        window.location.href = newUrl;
      }
    }
    // Emit the updated list
    this.selectedListsChange.emit(this.combinedSelected);
    setTimeout(() => {
      this.emitCloseDialog()
    }, 100)

  }

  joinRCLIds() {
    const rclIds = this.combinedSelected.map(item => item.rcl);
    const rclString = rclIds.join('-');
    return rclString;
  }

  emitCloseDialog() {
    this.closeDialog.emit()
  }

  filterResults(contentType: string) {
    const searchTextLower = this.searchTexts[contentType].toLowerCase();

    switch (contentType) {

      case 'retailers':
        this.filteredRetailers = this.retailersCopy.filter(retailer =>
          retailer.name.toLowerCase().includes(searchTextLower)
        );
        break;
      case 'rankings':
        // Filter the lists
        this.filteredLists = this.listsCopy.filter(list =>
          list.name.toLowerCase().includes(searchTextLower)
        );
        break;
      case 'sectors':
        // Filter the sectors
        this.filteredSectors = this.sectorsCopy.filter(sector =>
          sector.name.toLowerCase().includes(searchTextLower)
        );
        break;
      case 'clusters':
        // Filter the sectors
        this.filteredClusters = this.clustersCopy.filter(cluster =>
          cluster.name.toLowerCase().includes(searchTextLower)
        );
        break;
    }
    this.updateCombinedSelected();
  }

  clearFilter(contentType: string) {
    this.searchTexts[contentType] = '';
    this.filterResults(contentType);
  }

  onRetailerToggle(retailer: retailerData, event: any) {
    if (event.target.checked) {
      this.selectedRetailers.push(retailer);
    } else {
      this.selectedRetailers = this.selectedRetailers.filter(r => r !== retailer);
    }
    this.updateCombinedSelected();
  }

  onRankingToggle(ranking: rankingData, event: any) {
    const alreadySelected = this.selectedLists.some(r => r.rcl === ranking.rcl);

    if (event.target.checked) {

      if (!alreadySelected) {
        this.selectedLists.push(ranking);
      }
    } else {
      this.selectedLists = this.selectedLists.filter(r => r.rcl !== ranking.rcl);
    }

    this.updateCombinedSelected();
  }

  onRankingSelect(ranking: rankingData) {
    this.selectedLists = [ranking]; // single selection from dropdown
    this.updateCombinedSelected();
  }

  onSectorToggle(sector: sectorData, event: any) {
    if (event.target.checked) {
      this.selectedSectors.push(sector);
    } else {
      this.selectedSectors = this.selectedSectors.filter(s => s !== sector);
    }
    this.updateCombinedSelected();
  }

  onClusterToggle(cluster: clusterData, event: any) {
    if (event.target.checked) {
      this.selectedClusters.push(cluster);
    } else {
      this.selectedClusters = this.selectedClusters.filter(c => c !== cluster);
    }
    this.updateCombinedSelected();
  }

  updateCombinedSelected() {
    const all = [
      ...this.selectedRetailers,
      ...this.selectedLists,
      ...this.selectedSectors,
      ...this.selectedClusters,
    ];

    // Enforce a max of 5
    this.combinedSelected = all.slice(0, 5);
  }

  get isMaxSelected(): boolean {
    return this.combinedSelected.length >= 5;
  }

  removeSelection(filterName: string, selection: any): void {
    if (filterName === 'retailer') {
      this.selectedRetailers = this.selectedRetailers.filter(
        (item) => item !== selection
      );
    } else if (filterName === 'list') {
      this.selectedLists = this.selectedLists.filter(
        (item) => item !== selection
      );
      this.manualToggleRankingAverageCheckbox(false);
    } else if (filterName === 'sector') {
      this.selectedSectors = this.selectedSectors.filter(
        (item) => item !== selection
      );
    } else if (filterName === 'cluster') {
      this.selectedClusters = this.selectedClusters.filter(
        (item) => item !== selection
      );
    }
    this.updateCombinedSelected();
  }

  setTopCompareSelector() {
    // Step 0: Add empty entry if not present

    this.topCompareSelector = structuredClone(this.filteredLists);

    if (this.profileType !== 'list') {
      this.topCompareSelector = this.topCompareSelector.filter(
        (list: any) => list.r_id !== null
      );

      this.topCompareSelector.forEach((item: any) => {
        if (!item.name) {
          item.name = 'Any';
        }
      });
    }

    // Step 1: Sort
    this.topCompareSelector.sort((a: any, b: any) => {
      const nameA = a.name || "";
      const nameB = b.name || "";

      // 1. Empty name first
      if (!nameA && nameB) return -1;
      if (nameA && !nameB) return 1;

      // 2. Alphabetical
      const alphaCompare = nameA.localeCompare(nameB);
      if (alphaCompare !== 0) return alphaCompare;

      // 3. Numerical fallback if names are numeric
      const numA = parseFloat(nameA);
      const numB = parseFloat(nameB);

      const bothNumeric = !isNaN(numA) && !isNaN(numB);
      if (bothNumeric) return numA - numB;

      return 0;
    });
  }

  setInitialData(response: any) {
    this.retailers = response.data
      .filter((item: any) => item.type === 'retailer')
      .sort((a: any, b: any) => a.name.localeCompare(b.name));

    this.lists = response.data
      .filter((item: any) => item.type === 'list')
      .sort((a: any, b: any) => {
        if (a.name == null && b.name == null) return 0;
        if (a.name == null) return 1; // push nulls to the end
        if (b.name == null) return -1;
        return a.name.localeCompare(b.name);
      });

    this.sectors = response.data
      .filter((item: any) => item.type === 'sector')
      .sort((a: any, b: any) => a.name.localeCompare(b.name));

    this.clusters = response.data
      .filter((item: any) => item.type === 'cluster')
      .sort((a: any, b: any) => a.name.localeCompare(b.name));
  }

  setInitialFilters(response: any) {
    this.filteredRetailers = response.data
      .filter((item: any) => item.type === 'retailer')
      .sort((a: any, b: any) => a.name.localeCompare(b.name));

    this.filteredLists = response.data
      .filter((item: any) => item.type === 'list')
      .sort((a: any, b: any) => {
        if (a.name == null && b.name == null) return 0;
        if (a.name == null) return 1; // push nulls to the end
        if (b.name == null) return -1;
        return a.name.localeCompare(b.name);
      });

    this.filteredSectors = response.data
      .filter((item: any) => item.type === 'sector')
      .sort((a: any, b: any) => a.name.localeCompare(b.name));

    this.filteredClusters = response.data
      .filter((item: any) => item.type === 'cluster')
      .sort((a: any, b: any) => a.name.localeCompare(b.name));


    if (this.filteredLists.length) {
      this.onListSelectedByItem(this.defaultList);
      this.getCopies()
    }
  }

  getCopies() {
    this.retailersCopy = structuredClone(this.filteredRetailers);
    this.listsCopy = structuredClone(this.filteredLists);
    this.sectorsCopy = structuredClone(this.filteredSectors);
    this.clustersCopy = structuredClone(this.filteredClusters);
  }

  onListSelected(event: Event) {
    this.resetSelected();
    const currentType = this.tabs[this.activeTabIndex].contentType;
    this.clearFilter(currentType);

    const index = (event.target as HTMLSelectElement).value;
    const selectedItem = this.topCompareSelector[Number(index)];

    const matchedList = this.lists.find(list => list.rcl === selectedItem.rcl);

    if (matchedList) {
      this.onListSelectedByItem(matchedList);
      // this.onRankingSelect(matchedList);
      this.updateFilteredData(matchedList);
    } else {
      this.selectedLists = []; // Clear selection if no match
      this.onListSelectedByItem(null);
      this.updateFilteredData(null);
      this.updateCombinedSelected();;
    }

    this.currentTopList = this.topCompareSelector[this.selectedIndex];
    this.getCopies();
  }

  updateTabData() {
    const tabDefinitions = [
      {label: 'Retailers', color: '#1B3E78', contentType: 'retailers', content: this.filteredRetailers},
      // {label: 'Rankings', color: '#0CAD99', contentType: 'rankings', content: this.filteredLists},
      {label: 'Sectors', color: '#3792BF', contentType: 'sectors', content: this.filteredSectors},
      {label: 'Clusters', color: '#AACCFF', contentType: 'clusters', content: this.filteredClusters},
    ];

    for (const tabDef of tabDefinitions) {
      const existingTab = this.tabs.find(tab => tab.label === tabDef.label);

      if (existingTab) {
        // Update the existing tab content
        existingTab.content = tabDef.content;
      } else {
        // Add new tab
        this.tabs.push({
          label: 'Retailers',
          color: '#1B3E78',
          contentType: 'retailers',
          content: this.filteredRetailers
        })
        // this.tabs.push({label: 'Rankings', color: '#0CAD99', contentType: 'rankings', content: this.filteredLists})
        this.tabs.push({label: 'Sectors', color: '#3792BF', contentType: 'sectors', content: this.filteredSectors})
        this.tabs.push({label: 'Clusters', color: '#AACCFF', contentType: 'clusters', content: this.filteredClusters});
      }
    }
  }

  updateFilteredData(matchedList: rankingData | null) {
    if (matchedList === null) {

      // Filter retailers where in_list_id is null
      this.filteredRetailers = this.retailers.filter((retailer: any) => retailer.in_list_id === null);
      this.filteredSectors = this.sectors.filter((sector: any) => sector.in_list_id === null);
      this.filteredClusters = this.clusters.filter((cluster: any) => cluster.in_list_id === null);
      this.selectedIndex = 0;
      this.updateTabData();

    } else {
      // You can add logic here for when matchedList is not null
      this.filteredRetailers = this.retailers.filter((retailer: any) => retailer.in_list_id === matchedList.l_id);
      this.filteredSectors = this.sectors.filter((sector: any) => sector.in_list_id === matchedList.l_id);
      this.filteredClusters = this.clusters.filter((cluster: any) => cluster.in_list_id === matchedList.l_id);

      this.filteredLists = this.lists.filter(
        (list: any) => list.l_id === matchedList.l_id && list.r_id === null
      );
      this.updateTabData();
    }
    this.getCopies()
  }

  onListSelectedByItem(selectedItem: any) {
    const matchedList = this.lists.find(list => list?.rcl === selectedItem?.rcl);

    if (matchedList) {
      // this.onRankingSelect(matchedList);
      this.updateFilteredData(matchedList);
    } else {
      this.selectedLists = [];
      this.updateFilteredData(null);
      this.updateCombinedSelected();
    }
  }

  resetSelected() {
    this.selectedRetailers = [];
    this.selectedLists = [];
    this.selectedSectors = [];
    this.selectedClusters = [];
    this.updateCombinedSelected();
  }
}
